import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Account, TerminationReason } from '@monorepo/types';
import { useStore } from '@hub/helpers/use-store';
import { useQueryClient } from '@tanstack/react-query';
import { useDashboard } from '../../../contexts';
import { ACCOUNT_QUERY_KEYS } from '../../../hooks/constants';

/**
 * Manages termination reason modal open state, derived termination reason, and dismiss handler.
 * Shows the modal when the account is terminated for overnight/weekend violation and the user
 * has not yet seen it; on dismiss calls the mark-seen API and updates local/query cache.
 */
export const useTerminationReasonModalState = () => {
  // Context / Store
  const { account } = useDashboard();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    dataStore: { accountStore },
  } = useStore();

  // Local state
  const [isClosed, setIsClosed] = useState(false);

  // Derived values
  const isTerminatedForViolation =
    !!account?.state?.terminated &&
    (account.terminationReason === TerminationReason.OverNight ||
      account.terminationReason === TerminationReason.Weekend);

  const isOpen =
    isTerminatedForViolation &&
    !account?.config?.terminationReasonSeen &&
    !isClosed;

  const terminationReason = account?.terminationReason as
    | TerminationReason.OverNight
    | TerminationReason.Weekend
    | undefined;

  // Handlers
  const dismiss = (navigateTo?: string) => {
    setIsClosed(true);
    const tsAccountId = account?.tsAccountId;

    if (tsAccountId) {
      accountStore
        .markTerminationReasonAsSeen(tsAccountId)
        .then(() => {
          accountStore.setTerminationReasonSeenLocally(tsAccountId);
          queryClient.setQueryData<Account>(
            ACCOUNT_QUERY_KEYS.DASHBOARD(tsAccountId),
            (old) =>
              old
                ? {
                    ...old,
                    config: {
                      ...old.config,
                      hasSeen: old.config?.hasSeen ?? false,
                      terminationReasonSeen: true,
                    },
                  }
                : old,
          );
        })
        .catch((e) => {
          console.error(
            `Failed marking termination reason as seen: ${tsAccountId}`,
            e,
          );
        });
    }

    if (navigateTo) {
      navigate(navigateTo);
    }
  };

  return { isOpen, terminationReason, dismiss };
};
